import { usePartyLicensesQuery } from "Api/Queries/Licenses/usePartyLicensesQuery";
import { useCompleteRoleOnboardingMutation } from "Api/Mutations/Roles/useCompleteRoleOnboardingMutation";
import { useGetContextsQuery } from "Auth/Api/Queries/Context/useGetContextsQuery";
import { getLicenseResumeState } from "Components/Licenses/licenseUtils";
import { PaidLicenseStepContent } from "Components/Onboarding/shared/PaidLicenseStepContent";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { InitiateQRPaymentComponent } from "Components/QRPayment/InitiateQRPaymentComponent";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";

const COMPONENT_RESOURCES = Resources.Roles.AddRole.Completion;

type Props = {
  onNextStepRequested: () => void;
};

/**
 * Add-role variant of the hunter's final step: the legacy one submits the person's KYC
 * (ProcessUserVerification), which an add-role hunter finished long ago as a Customer — here the
 * wizard ends by activating the role grant instead. License payment confirmation stays independent.
 */
export const AddRoleManualLicensePaymentStep = ({
  onNextStepRequested: _unused,
}: Props) => {
  const currentUser = useLoggedInUser();
  const { t } = useResource();
  const { data } = useGetContextsQuery();
  const activeIdentityUserPublicID = data?.activeIdentityUserPublicID;

  const { mutate: completeRole, isPending } = useCompleteRoleOnboardingMutation(
    {
      onSuccess: () => {
        toast.success(t(COMPONENT_RESOURCES.Success));
        window.location.replace(appUrl("homepage"));
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.Error));
      },
    },
  );

  const {
    data: { items: licenses = [] } = {},
    isLoading,
    isError,
  } = usePartyLicensesQuery(currentUser?.partyPublicID);

  if (isLoading) {
    return <CenteredCircularProgress />;
  }

  if (isError) {
    return <ErrorText>{t(Resources.Errors.RequestError)}</ErrorText>;
  }

  const handleCompleteRole = () => {
    if (activeIdentityUserPublicID) {
      completeRole(activeIdentityUserPublicID);
    }
  };

  const resumeState = getLicenseResumeState(licenses);

  if (resumeState.kind === "none") {
    return <ErrorText>{t(Resources.Errors.RequestError)}</ErrorText>;
  }

  if (resumeState.kind === "paid") {
    return (
      <PaidLicenseStepContent
        license={resumeState.license}
        onContinue={handleCompleteRole}
        isSubmitting={isPending}
      />
    );
  }

  const { unpaidLicenses } = resumeState;
  const licenseToPay = unpaidLicenses[0];

  if (!licenseToPay?.transactionPublicID || unpaidLicenses.length > 1) {
    return <ErrorText>{t(Resources.Errors.RequestError)}</ErrorText>;
  }

  return (
    <FullHeightForm>
      <StepBox
        submitButtonLabel={t(Resources.Common.Sent)}
        onSubmitClick={handleCompleteRole}
        isSubmitting={isPending}
      >
        <InitiateQRPaymentComponent
          publicID={licenseToPay.transactionPublicID}
        />
      </StepBox>
    </FullHeightForm>
  );
};
